import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { wibToday, fmtWib } from "@/lib/bizdate";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState, StatusBadge } from "@/components/common";
import { rupiah, numberID, formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PRDetail } from "./PurchaseDetail";
import { toast } from "sonner";
import { Plus, FileText, MoreVertical, X, Info } from "lucide-react";

export default function PurchaseRequests() {
  const [detail, setDetail] = useState(null);
  const [cancelPr, setCancelPr] = useState(null);
  const { can } = useAuth();
  const [rows, setRows] = useState(null);
  const [wh, setWh] = useState([]);
  const [open, setOpen] = useState(false);
  const canCreate = can("purchasing.pr.create");
  const canApprove = can("purchasing.pr.approve");
  const load = () => { setRows(null); api.get("/purchasing/purchase-requests").then((r) => setRows(r.data)); };
  useEffect(() => { api.get("/inventory/warehouses").then((r) => setWh(r.data)); load(); }, []);
  const act = async (pr, action) => {
    try { await api.post(`/purchasing/purchase-requests/${pr.id}/${action}`); toast.success("Berhasil"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div>
      <PageHeader title="Permintaan Pembelian" subtitle="Buat & setujui permintaan pembelian (PR) sebelum menjadi PO." testId="pr-header">
        {canCreate && <Button onClick={() => setOpen(true)} data-testid="add-pr-btn" className="bg-indigo-600 hover:bg-indigo-500"><Plus className="mr-1.5 h-4 w-4" /> PR Baru</Button>}
      </PageHeader>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        {!rows ? <Loading /> : rows.length === 0 ? <EmptyState title="Belum ada PR" icon={FileText} /> : (
          <Table>
            <TableHeader><TableRow><TableHead>No PR</TableHead><TableHead>Tanggal</TableHead><TableHead>Pemohon</TableHead><TableHead>Tujuan</TableHead><TableHead className="text-right">Baris Item</TableHead><TableHead className="text-right">Estimasi</TableHead><TableHead>Urgensi</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((pr) => (
                <TableRow key={pr.id} data-testid={`pr-row-${pr.pr_no}`}>
                  <TableCell className="font-mono text-xs">{pr.pr_no}</TableCell>
                  <TableCell className="text-xs text-slate-500">{pr.tanggal}</TableCell>
                  <TableCell>{pr.pemohon}</TableCell>
                  <TableCell className="text-slate-500">{pr.warehouse_name}</TableCell>
                  <TableCell className="text-right">{pr.items.length}</TableCell>
                  <TableCell className="text-right">{rupiah(pr.est_grand_total)}</TableCell>
                  <TableCell><span className={pr.urgensi === "Tinggi" ? "text-rose-600 font-semibold" : "text-slate-500"}>{pr.urgensi}</span></TableCell>
                  <TableCell><div className="flex items-center gap-1"><StatusBadge status={pr.status} />{pr.status === "Dibatalkan" && <CancelBadgePopover pr={pr} />}</div></TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setDetail(pr.id)} data-testid={`pr-detail-${pr.pr_no}`}>Detail</DropdownMenuItem>
                        {canCreate && pr.status === "Draft" && <DropdownMenuItem onClick={() => act(pr, "submit")} data-testid={`pr-submit-${pr.pr_no}`}>Ajukan</DropdownMenuItem>}
                        {canApprove && pr.status === "Menunggu Persetujuan" && <DropdownMenuItem onClick={() => act(pr, "approve")} data-testid={`pr-approve-${pr.pr_no}`}>Setujui</DropdownMenuItem>}
                        {canApprove && pr.status === "Menunggu Persetujuan" && <DropdownMenuItem onClick={() => act(pr, "reject")} data-testid={`pr-reject-${pr.pr_no}`}>Tolak</DropdownMenuItem>}
                        {canApprove && pr.status === "Disetujui" && <DropdownMenuItem onClick={() => setCancelPr(pr)} data-testid={`pr-cancel-${pr.pr_no}`} className="text-rose-600">Batalkan PR</DropdownMenuItem>}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      {open && <PRDialog wh={wh} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {detail && <PRDetail id={detail} onClose={() => setDetail(null)} />}
      {cancelPr && <PRCancelDialog pr={cancelPr} onClose={() => setCancelPr(null)} onDone={() => { setCancelPr(null); load(); }} />}
    </div>
  );
}

function CancelBadgePopover({ pr }) {
  const ch = (pr.status_history || []).find((h) => h.status === "Dibatalkan");
  const by = pr.cancelled_by || ch?.by || "Tidak diketahui";
  const at = pr.cancelled_at || ch?.at;
  const reason = pr.cancel_reason || ch?.note || "Tidak tercatat";
  const shortReason = reason.length > 140 ? reason.slice(0, 140) + "…" : reason;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" aria-label="Info pembatalan PR" data-testid={`pr-cancel-info-${pr.pr_no}`} className="rounded-full p-0.5 text-rose-500 hover:text-rose-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400">
          <Info className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 text-sm" data-testid={`pr-cancel-popover-${pr.pr_no}`}>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-rose-600">Pembatalan PR</p>
        <div className="space-y-1">
          <div><span className="text-slate-500">Alasan: </span><span className="font-medium text-slate-800">{shortReason}</span></div>
          <div><span className="text-slate-500">Dibatalkan oleh: </span><span className="font-medium text-slate-800">{by}</span></div>
          <div><span className="text-slate-500">Waktu (WIB): </span><span className="font-medium text-slate-800">{at ? fmtWib(at) : "Tidak tersedia"}</span></div>
        </div>
        <p className="mt-2 text-[11px] text-slate-400">Buka Detail PR untuk informasi lengkap.</p>
      </PopoverContent>
    </Popover>
  );
}

function PRCancelDialog({ pr, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) return toast.error("Alasan pembatalan wajib diisi");
    setBusy(true);
    try {
      await api.post(`/purchasing/purchase-requests/${pr.id}/cancel`, { reason: reason.trim() });
      toast.success(`${pr.pr_no} dibatalkan`);
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="pr-cancel-dialog">
        <DialogHeader><DialogTitle>Batalkan PR · {pr.pr_no}</DialogTitle></DialogHeader>
        <div className="space-y-2 text-sm text-slate-600">
          <p>PR <b>{pr.pr_no}</b> ({pr.status}) akan dibatalkan. Hanya diperbolehkan bila PR belum memiliki PO pembelian apa pun. Tindakan ini tidak mengubah stok, saldo, jurnal, atau data historis.</p>
          <div>
            <Label className="text-xs text-slate-500">Alasan Pembatalan (wajib)</Label>
            <Textarea className="mt-1" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="pr-cancel-reason" placeholder="Tuliskan alasan pembatalan..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="pr-cancel-abort">Batal</Button>
          <Button onClick={submit} disabled={busy || !reason.trim()} data-testid="pr-cancel-confirm" className="bg-rose-600 hover:bg-rose-500">{busy ? "..." : "Batalkan PR"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PRDialog({ wh, onClose, onSaved }) {
  const today = wibToday();
  const [f, setF] = useState({ tanggal: today, departemen: "", warehouse_id: "", urgensi: "Normal", alasan: "", notes: "" });
  const BLANK = { item_id: "", item_name: "", category: "", qty: 1, unit: "", est_price: 0, estimated_unit_price: 0, tracking_mode: "", estimated_weight_kg: 0, estimated_price_per_kg: 0, notes: "" };
  const [items, setItems] = useState([{ ...BLANK }]);
  const [whItems, setWhItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [cwEnabled, setCwEnabled] = useState(false);
  useEffect(() => { api.get("/purchasing/catch-weight/config").then((r) => setCwEnabled(!!r.data.fabric_catch_weight_enabled)).catch(() => setCwEnabled(false)); }, []);
  useEffect(() => {
    if (!f.warehouse_id) { setWhItems([]); return; }
    api.get("/inventory/items", { params: { warehouse_id: f.warehouse_id, status: "active" } }).then((r) => setWhItems(r.data));
  }, [f.warehouse_id]);
  const pickItem = (i, id) => {
    const it = whItems.find((x) => x.id === id);
    const tm = cwEnabled ? (it?.tracking_mode || "") : "";
    setItems(items.map((x, idx) => idx === i ? { ...x, item_id: id, item_name: it?.name || "", unit: it?.unit || "", category: it?.category || "", tracking_mode: tm } : x));
  };
  const save = async () => {
    if (!f.warehouse_id) return toast.error("Pilih inventory tujuan");
    if (items.some((i) => !i.item_name || Number(i.qty) <= 0)) return toast.error("Lengkapi item & qty > 0");
    setSaving(true);
    try {
      await api.post("/purchasing/purchase-requests", { ...f, items: items.map((i) => { if (i.tracking_mode === "CATCH_WEIGHT_ROLL") { return { ...i, qty: Number(i.qty), requested_roll_qty: Number(i.qty), estimated_weight_kg: Number(i.estimated_weight_kg || 0), estimated_price_per_kg: Number(i.estimated_price_per_kg || 0), notes: i.notes || "" }; } const up = Number(i.estimated_unit_price || i.est_price || 0); return { ...i, qty: Number(i.qty), estimated_unit_price: up, est_price: up, price_source: up > 0 ? "MANUAL" : "UNAVAILABLE", notes: i.notes || "" }; }) });
      toast.success("PR dibuat (Draft)"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle className="font-display">Permintaan Pembelian Baru</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs text-slate-500">Tanggal</Label><Input type="date" className="mt-1" value={f.tanggal} onChange={(e) => setF({ ...f, tanggal: e.target.value })} data-testid="pr-tanggal" /></div>
          <div><Label className="text-xs text-slate-500">Inventory Tujuan</Label><Select value={f.warehouse_id} onValueChange={(v) => setF({ ...f, warehouse_id: v })}><SelectTrigger className="mt-1" data-testid="pr-warehouse"><SelectValue placeholder="Pilih..." /></SelectTrigger><SelectContent>{wh.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs text-slate-500">Departemen</Label><Input className="mt-1" value={f.departemen} onChange={(e) => setF({ ...f, departemen: e.target.value })} /></div>
          <div><Label className="text-xs text-slate-500">Urgensi</Label><Select value={f.urgensi} onValueChange={(v) => setF({ ...f, urgensi: v })}><SelectTrigger className="mt-1" data-testid="pr-urgensi"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Normal">Normal</SelectItem><SelectItem value="Tinggi">Tinggi</SelectItem></SelectContent></Select></div>
        </div>
        <div>
          <div className="mb-2 flex items-center justify-between"><Label className="text-sm font-semibold">Item</Label><Button size="sm" variant="outline" disabled={!f.warehouse_id} onClick={() => setItems([...items, { ...BLANK }])}><Plus className="mr-1 h-3.5 w-3.5" /> Item</Button></div>
          {!f.warehouse_id ? <p className="text-xs text-slate-400">Pilih inventory tujuan dahulu.</p> : items.map((it, i) => {
            const cw = it.tracking_mode === "CATCH_WEIGHT_ROLL";
            const amt = (Number(it.estimated_weight_kg) || 0) * (Number(it.estimated_price_per_kg) || 0);
            return (
            <div key={i} className="mb-2 rounded border border-slate-100 p-2" data-testid={`pr-line-${i}`}>
              <div className="grid grid-cols-12 items-center gap-2">
                <div className="col-span-5"><Select value={it.item_id} onValueChange={(v) => pickItem(i, v)}><SelectTrigger data-testid={`pr-item-${i}`}><SelectValue placeholder="Pilih material..." /></SelectTrigger><SelectContent>{whItems.map((x) => <SelectItem key={x.id} value={x.id}>{x.name} <span className="text-xs text-slate-400">({x.unit})</span></SelectItem>)}</SelectContent></Select></div>
                <Input type="number" min="0" className="col-span-3" placeholder={cw ? "Qty Roll" : "Qty"} value={it.qty} onChange={(e) => setItems(items.map((x, idx) => idx === i ? { ...x, qty: e.target.value } : x))} data-testid={`pr-qty-${i}`} />
                {!cw && <Input type="number" min="0" className="col-span-3" placeholder="Est. Harga" value={it.estimated_unit_price} onChange={(e) => setItems(items.map((x, idx) => idx === i ? { ...x, estimated_unit_price: e.target.value } : x))} data-testid={`pr-price-${i}`} />}
                {cw && <span className="col-span-3 text-center text-xs font-medium text-indigo-600">Catch-weight (ROLL+KG)</span>}
                <Button variant="ghost" size="sm" className="col-span-1 text-rose-500" onClick={() => setItems(items.filter((_, idx) => idx !== i))}><X className="h-4 w-4" /></Button>
              </div>
              {cw && (
                <div className="mt-2 grid grid-cols-12 items-center gap-2">
                  <Input type="number" min="0" className="col-span-3" placeholder="Estimasi KG" value={it.estimated_weight_kg} onChange={(e) => setItems(items.map((x, idx) => idx === i ? { ...x, estimated_weight_kg: e.target.value } : x))} data-testid={`pr-estkg-${i}`} />
                  <Input type="number" min="0" className="col-span-3" placeholder="Harga / KG" value={it.estimated_price_per_kg} onChange={(e) => setItems(items.map((x, idx) => idx === i ? { ...x, estimated_price_per_kg: e.target.value } : x))} data-testid={`pr-ppk-${i}`} />
                  <span className="col-span-6 text-xs text-slate-600" data-testid={`pr-estamt-${i}`}>Estimasi: <b>{rupiah(amt)}</b> · <span className="text-amber-600">Berat masih estimasi. Berat final ditentukan pada Penerimaan Barang.</span></span>
                </div>
              )}
            </div>
          ); })}
        </div>
        <div><Label className="text-xs text-slate-500">Alasan / Catatan</Label><Textarea className="mt-1" rows={2} value={f.alasan} onChange={(e) => setF({ ...f, alasan: e.target.value })} /></div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={saving} data-testid="save-pr-btn" className="bg-indigo-600 hover:bg-indigo-500">{saving ? "..." : "Simpan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
